import type {
  DiscountDTO,
  KlarnaOrderLine,
  MinorUnit,
  Option,
  StorefrontOrderLine
} from 'ecosystem'
import {
  type BuyerVatRates,
  createMinorUnit,
  getTax,
  getVatRateByCountryCode,
  roundNumber,
  shippingForBuyer,
  storeGrossRebaser,
  sumArrayOfNumbers
} from 'shared-utils'
import { orderLineValueCalculator } from './payment'

export const klarnaOrderLineFactory = (
  items: StorefrontOrderLine[],
  cartDiscountCode: DiscountDTO | null,
  countryCode?: string,
  buyerVat?: Option<BuyerVatRates>
) => {
  // Product rows carry each line's own `vatRate`, already resolved for the
  // buyer upstream. Service rows have no rate of their own, so they take the
  // store's rate — resolved here for the same buyer so the two agree.
  const domesticRate = getVatRateByCountryCode(countryCode)
  const taxRate = createMinorUnit(buyerVat?.resolveRate(domesticRate) ?? domesticRate)
  const productOrderLines = items.map((i) => mapStorefrontProductsIntoKlarna(i, cartDiscountCode))
  const serviceOrderLines = servicesOrderLineFactory(
    items,
    taxRate,
    storeGrossRebaser(domesticRate, buyerVat)
  )
  const orderLines = [...productOrderLines, ...serviceOrderLines]
  const orderAmount = sumArrayOfNumbers(
    orderLines.map((item: KlarnaOrderLine) => item.total_amount)
  )
  // The same basket net of VAT, in the units `orderAmount` uses. Feeds
  // `netOrderAmount` on the shipping factories so a delivery rate configured
  // ex-VAT is evaluated against the figure it was written on. Derived from the
  // rows rather than from the basket, so the discount code and this provider's
  // unit convention are already applied.
  const netOrderAmount =
    orderAmount -
    sumArrayOfNumbers(orderLines.map((item: KlarnaOrderLine) => item.total_tax_amount))
  return {
    orderLines,
    orderAmount,
    netOrderAmount,
    productOrderLines,
    taxRate,
    /** Move a carrier's VAT-inclusive quote onto this buyer's rate. Unit-agnostic
     *  (a pure ratio), so minor units pass through unchanged. */
    rebaseShipping: (grossQuote: number) =>
      shippingForBuyer(grossQuote, domesticRate, buyerVat).cost
  }
}

export const servicesOrderLineFactory = (
  items: StorefrontOrderLine[],
  taxRate: MinorUnit,
  rebaseServicePrice: (grossMajor: number) => number = (p) => p
): KlarnaOrderLine[] => {
  return items
    .map((item) =>
      item.services.map((service) => {
        const quantity = service.quantity
        let unitPrice = rebaseServicePrice(service.service.price) * 100
        const discountFactor = rebaseServicePrice(service.service.discountPrice) * 100
        let totalDiscountAmount = 0
        let totalAmount = 0

        /*
          Basic formula for calculating price of service:
          service.price + service.discountPrice * (quantity - 1)
         */
        if (quantity > 0) {
          totalAmount = unitPrice + (quantity - 1) * discountFactor

          if (!unitPrice && discountFactor) {
            unitPrice = discountFactor
          }

          totalDiscountAmount = unitPrice * quantity - totalAmount
        }

        return {
          type: 'surcharge' as const,
          name: service.service.name,
          tax_rate: taxRate,
          quantity,
          total_amount: totalAmount,
          unit_price: unitPrice,
          total_tax_amount: roundNumber(getTax(totalAmount, taxRate, true), 0),
          total_discount_amount: totalDiscountAmount,
          merchant_data: service.service.id
        }
      })
    )
    .flat()
}

/**
 * Product rows pass through verbatim — no VAT conversion here.
 *
 * `item.vatRate` and `item.unitPrice` are already resolved for this buyer (see
 * {@link StorefrontOrderLine}), so a zero-rated company arrives with rate 0 and
 * a net price and needs nothing further. Re-basing again here would convert
 * twice. Rows without a rate of their own — services, shipping — are the ones
 * that do need converting, and that happens in the factories above.
 */
export const mapStorefrontProductsIntoKlarna = (
  item: StorefrontOrderLine,
  cartDiscountCode: DiscountDTO | null
): KlarnaOrderLine => {
  const taxRate = createMinorUnit(item.vatRate)
  const { quantity, totalDiscountAmount, totalAmount, unitPrice } = orderLineValueCalculator(
    item,
    cartDiscountCode
  )
  const totalTaxAmount = roundNumber(getTax(totalAmount, taxRate, true), 0)

  return {
    type: 'physical',
    name: item.product.name,
    tax_rate: taxRate,
    quantity,
    total_amount: totalAmount,
    unit_price: unitPrice,
    total_tax_amount: totalTaxAmount,
    total_discount_amount: totalDiscountAmount,
    merchant_data: item.product.articleNumber
  }
}
